// Shared utilities and content for /cidco public website
// Visual identity: Navy primary (#0B2447), white/light-gray secondary, orange CTAs, cyan highlights.
// Typography: Poppins (display) + Inter (body).
import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export const CIDCO_COLORS = {
  navy: "#0B2447",
  navyDark: "#06183A",
  navyLight: "#1B3567",
  paper: "#F4F6FA",
  border: "#E2E8F0",
  orange: "#FF6B1A",
  orangeDark: "#E55400",
  orangeLight: "#FFE9D6",
  cyan: "#06B6D4",
  cyanDark: "#0891B2",
  cyanLight: "#CFFAFE",
  ink: "#0F172A",
  inkSoft: "#475569",
};

export function slugify(name) {
  return String(name || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function formatDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
}

export function PageHero({ eyebrow, title, subtitle, accent = "navy" }) {
  const bg =
    accent === "cyan" ? "from-[#06B6D4] via-[#0891B2] to-[#0B2447]" :
    accent === "orange" ? "from-[#FF6B1A] via-[#E55400] to-[#0B2447]" :
    "from-[#0B2447] via-[#1B3567] to-[#06183A]";
  return (
    <section className={`relative bg-gradient-to-br ${bg} text-white py-16 md:py-24 overflow-hidden`} data-testid="cid-page-hero">
      {/* Subtle dotted grid + soft blob */}
      <div className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }} />
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#06B6D4]/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4">
        {eyebrow && (
          <div className="text-[10px] tracking-[0.4em] uppercase font-semibold text-[#67E8F9] mb-3 font-['Inter',sans-serif]">
            {eyebrow}
          </div>
        )}
        <h1 className="font-['Poppins',sans-serif] font-bold text-4xl md:text-6xl tracking-tight max-w-4xl leading-[1.05]">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-4 max-w-2xl text-white/85 text-base md:text-lg font-['Inter',sans-serif]">{subtitle}</p>
        )}
      </div>
    </section>
  );
}

export function PageBody({ children, className = "" }) {
  return (
    <section className={`max-w-7xl mx-auto px-4 py-12 md:py-16 ${className}`}>
      {children}
    </section>
  );
}

export function Lightbox({ photos, idx, onIdx, onClose }) {
  const cur = photos[idx];
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onIdx((idx - 1 + photos.length) % photos.length);
      if (e.key === "ArrowRight") onIdx((idx + 1) % photos.length);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [idx, photos.length, onClose, onIdx]);
  if (!cur) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="cid-lightbox">
      <button onClick={onClose} className="absolute top-5 right-5 text-white/80 hover:text-white p-2" aria-label="Close">
        <X size={22} />
      </button>
      {photos.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onIdx((idx - 1 + photos.length) % photos.length); }} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-full" aria-label="Previous"><ChevronLeft size={24} /></button>
          <button onClick={(e) => { e.stopPropagation(); onIdx((idx + 1) % photos.length); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-full" aria-label="Next"><ChevronRight size={24} /></button>
        </>
      )}
      <figure className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
        <img src={cur.image_url || cur.url} alt={cur.caption || ""} className="max-w-full max-h-[80vh] object-contain" />
        {cur.caption && <figcaption className="mt-3 text-center text-white/90 text-sm">{cur.caption}</figcaption>}
      </figure>
    </div>
  );
}

// Lightweight renderer for institute Vision/Mission/About content.
// Backwards-compatible: if the stored value is plain text (no tags), preserves newlines.
export function renderInstituteHtml(content) {
  if (!content) return null;
  const isHtml = /<\/?[a-z][\s\S]*?>/i.test(content);
  const html = isHtml
    ? content
    : content
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br/>");
  return html;
}

// Minimal static fallback copy used when CMS does not yet provide vision/mission/about.
export const STATIC_CONTENT = {
  about_intro:
    "Marathwada Institute of Technology, CIDCO is a flagship engineering campus of Gramaudyogik Shikshan Mandal — established to nurture industry-ready technologists for India's growing manufacturing, digital, and infrastructure economy. Across our specialised undergraduate and postgraduate programmes, students learn from research-active faculty, design in modern labs, and ship real projects with industry partners.",
  vision:
    "To be a benchmark institution in technological education and applied research — graduating ethical, entrepreneurial engineers who solve problems that matter, locally and globally.",
  mission:
    "Deliver an industry-aligned, outcome-based curriculum; foster a culture of curiosity, design, and rigour; build research and innovation partnerships across academia and industry; and develop graduates whose technical depth is matched by clarity of thought and integrity of action.",
};
